import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery, useApolloClient } from '@apollo/react-hooks';
import dayjs from 'dayjs';
import { Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useFeatures } from 'flagged';

import type { RootState } from '../../store/store';
import { name as gameFacetSelector, setPage } from '../../store/reducers/gameFacet';
import { useDebouncedEffect } from '../../hooks';
import { listGames, ferPresenceQuery } from './apiQueries';
import { Game, StringFilterInput, USE_DRM } from '../../utils/types';
import GamesList from './GamesList';
import GamesFilters from './GamesFilters';
import { getCurrentDateString } from '../../utils/dateTransform';

const PAGE_SIZE = 30;
const SORT_DIRECTION = 'DESC';
const AUTO_REFETCH_PERIOD = 30 * 1000; // 30 seconds in milliseconds
const LIMIT = 1000;

type FerItem = {
	__typename: string;
	ferId: string;
};

const Games: FC = () => {
	const dispatch = useDispatch();
	const client = useApolloClient();
	const [ferPresenceMap, setFerPresenceMap] = useState<Map<string, boolean>>(new Map());
	const [ferFetchingInProgress, setFerFetchingInProgress] = useState(false);

	const { drmMode: drmModeFlag } = useFeatures();
	const drmMode = drmModeFlag?.toString();

	const useDrm = drmMode === 'yes' ? USE_DRM.YES : drmMode === 'both' ? USE_DRM.BOTH : USE_DRM.NO;

	// All these search facets come from the gameFacetSelector in the redux store
	const { id, title, sport, startTime, endTime, hasAirings, status, league, page, count, autoRefetch, faceted } = useSelector(
		(storedState: RootState) => storedState[gameFacetSelector]
	);

	// debouncedId and debouncedTitle apply a 500ms pause on keyboard input of id and title fields
	// initialised with gameFacetSelector props to avoid debouncing page reload
	const [debouncedId, setDebouncedId] = useDebouncedEffect(id || '', 500);
	const [debouncedTitle, setDebouncedTitle] = useDebouncedEffect(title || '', 500);

	// on update set the debounced values
	useEffect(() => {
		setDebouncedId(id);
		setDebouncedTitle(title);
	}, [id, title, setDebouncedId, setDebouncedTitle]);

	// Set up query filters
	const filters: GameFiltersInput = {};
	if (debouncedId) filters.pk = { beginsWith: `game#${debouncedId}` }; // note: debouncedId
	if (sport) filters.sport = { beginsWith: sport };
	if (debouncedTitle) filters.title = { contains: debouncedTitle }; // note: debouncedTitle

	const calcEndTime =
		startTime && endTime && dayjs(startTime).isBefore(dayjs(endTime).add(1, 'day')) ? dayjs(endTime).add(1, 'day').format('YYYY-MM-DD') : '';

	// The main query hook
	const { loading, error, data, refetch, startPolling, stopPolling } = useQuery(listGames, {
		variables: {
			startTime,
			endTime: calcEndTime,
			nextToken: null,
			limit: LIMIT,
			sortDirection: SORT_DIRECTION,
			filter: Object.keys(filters).length > 0 ? filters : null
		},
		pollInterval: autoRefetch ? AUTO_REFETCH_PERIOD : 0
	});

	// Configure autoRefetch on the useQuery hook
	useEffect(() => {
		if (autoRefetch) {
			startPolling(AUTO_REFETCH_PERIOD);
		} else {
			stopPolling();
		}
	}, [startPolling, stopPolling, autoRefetch]);

	// Requery when filters update
	useEffect(() => {
		// To prevent false triggering we have to compose the filters again, inside useEffect
		const ueFilters: GameFiltersInput = {};
		if (debouncedId) ueFilters.pk = { beginsWith: `game#${debouncedId}` }; // note: debouncedId
		if (sport) ueFilters.sport = { beginsWith: sport };
		if (debouncedTitle) ueFilters.title = { contains: debouncedTitle }; // note: debouncedTitle

		refetch({
			startTime,
			endTime: calcEndTime,
			nextToken: null,
			limit: LIMIT,
			sortDirection: SORT_DIRECTION,
			filter: Object.keys(ueFilters).length > 0 ? ueFilters : null
		});
	}, [startTime, calcEndTime, refetch, debouncedId, sport, debouncedTitle]);

	// Batch fetch FER presence when hasAirings filter is active
	useEffect(() => {
		if (ferFetchingInProgress) return;

		const gameIds = (data?.listGames?.items || []).map((g: Game) => g.id);
		const missingIds = gameIds.filter((id: string) => !ferPresenceMap.has(id));

		if (missingIds.length === 0) return;

		setFerFetchingInProgress(true);

		// Batch query FER data for all games
		Promise.all(
			missingIds.map((gameId: string) =>
				client.query({
					query: ferPresenceQuery,
					variables: { gameId }
				})
			)
		)
			.then((results) => {
				const newMap = new Map(ferPresenceMap);
				results.forEach((result, index) => {
					const gameId = missingIds[index];
					const ferItems = result.data?.listFers?.items ?? [];
					const hasFers = ferItems.filter((item: FerItem) => item.__typename === 'Fer').length > 0;
					newMap.set(gameId, hasFers);
				});
				setFerPresenceMap(newMap);
				setFerFetchingInProgress(false);
			})
			.catch(() => {
				setFerFetchingInProgress(false);
			});
	}, [hasAirings, ferPresenceMap, client, ferFetchingInProgress, data]);

	const nextToken = data?.listGames.nextToken || null;

	const handlePageChange = (newPage: number): void => {
		dispatch(setPage(newPage));
	};

	const renderGameList = (): JSX.Element => {
		if (loading) return <Typography>Loading...</Typography>;
		if (error) return <Typography>Error loading data from the server</Typography>;

		const filteredGames = data?.listGames.items.filter((game: Game): boolean => {
			if (hasAirings) {
				// Filter games that have airings OR FERs (FER data fetched via batch query)
				const hasAiringItems = game.airings && game.airings.items.length > 0;
				const hasFerItems = ferPresenceMap.get(game.id) ?? false;
				if (!hasAiringItems && !hasFerItems) return false;
			}
			if (status && game.status !== status) return false;
			if (league && game.league?.name !== league) return false;
			return true;
		});

		if (!filteredGames || (filteredGames.length === 0 && !nextToken)) return <Typography>No games found</Typography>;

		filteredGames.sort((a: Game, b: Game) => {
			if (!a.startTime) return -1;
			if (!b.startTime) return 1;
			return dayjs(a!.startTime).isBefore(dayjs(b!.startTime)) ? 1 : -1;
		});

		const currentPage = filteredGames.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

		return (
			<GamesList
				view={startTime === getCurrentDateString() ? 'TODAY' : 'ALL'}
				pageSize={PAGE_SIZE}
				page={page}
				games={currentPage}
				count={count || filteredGames.length}
				onPageChange={handlePageChange}
				refetch={refetch}
				useDrm={useDrm}
				ferPresenceMap={ferPresenceMap}
			/>
		);
	};

	return (
		<>
			<GamesFilters />
			{!loading && nextToken && faceted && (
				<Alert severity='warning' style={{ marginTop: -10, marginBottom: 10 }}>
					Please consider narrowing your search, which is slow and too broad for accurate paginated display.
				</Alert>
			)}
			{renderGameList()}
		</>
	);
};

type GameFiltersInput = {
	pk?: StringFilterInput | null;
	sport?: StringFilterInput | null;
	title?: StringFilterInput | null;
};

export default Games;
